import React, { useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Layers, Plus, Trash2 } from 'lucide-react';
import type { ProposalNodeSet, ProposalPhase, ProposalPhaseStatus } from '../../types';
import {
  computePhaseCommitAmount,
  getOrderedPhases,
  getPhaseNodeSets,
  getPhaseStatusLabel,
  validateProposalPhases,
} from '../../utils/proposalPhases';
import './ProposalPhasePanel.css';

interface ProposalPhasePanelProps {
  phasingEnabled: boolean;
  phases: ProposalPhase[];
  nodeSets: ProposalNodeSet[];
  profitMargin: number;
  /** Proposal has been signed — committed scope is locked. */
  proposalApproved?: boolean;
  onTogglePhasing: (enabled: boolean) => void;
  onAddPhase: () => void;
  onRenamePhase: (phaseId: string, name: string) => void;
  onMovePhase: (phaseId: string, direction: -1 | 1) => void;
  onDeletePhase: (phaseId: string) => void;
  onSetPhaseStatus: (phaseId: string, status: ProposalPhaseStatus) => void;
}

const fmt = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const ProposalPhasePanel: React.FC<ProposalPhasePanelProps> = ({
  phasingEnabled,
  phases,
  nodeSets,
  profitMargin,
  proposalApproved = false,
  onTogglePhasing,
  onAddPhase,
  onRenamePhase,
  onMovePhase,
  onDeletePhase,
  onSetPhaseStatus,
}) => {
  const proposalLike = { phases, nodeSets, profitMargin, phasingEnabled };
  const ordered = getOrderedPhases(proposalLike);
  const hasCommitted = phases.some(p => p.status === 'committed');
  const problems = phasingEnabled ? validateProposalPhases(proposalLike) : [];
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches,
  );

  const phaseSummary = phasingEnabled
    ? `${ordered.length} phase${ordered.length === 1 ? '' : 's'}`
    : '';

  return (
    <div className={`proposal-phase-panel ${collapsed ? 'collapsed' : ''}`}>
      <div className="proposal-phase-panel-header">
        <div className="proposal-phase-panel-header-left">
          <button
            type="button"
            className="proposal-phase-collapse-btn"
            onClick={() => setCollapsed(c => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand phased project panel' : 'Collapse phased project panel'}
          >
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          <div className="proposal-phase-panel-title">
            <Layers size={16} aria-hidden />
            <span>Phased project</span>
            <span className="proposal-phase-beta">Beta</span>
          </div>
          {collapsed && phasingEnabled && (
            <span className="proposal-phase-panel-summary">{phaseSummary}</span>
          )}
        </div>
        <label
          className={`proposal-phase-toggle ${hasCommitted || proposalApproved ? 'locked' : ''}`}
          title={
            proposalApproved
              ? 'Phasing is locked — this proposal has been signed'
              : hasCommitted
                ? 'Cannot turn phasing off — a phase has already been committed'
                : 'Group structures into phases the client commits to incrementally'
          }
        >
          <input
            type="checkbox"
            checked={phasingEnabled}
            disabled={hasCommitted || proposalApproved}
            onChange={e => onTogglePhasing(e.target.checked)}
          />
          <span>{phasingEnabled ? 'On' : 'Off'}</span>
        </label>
      </div>

      {phasingEnabled && !collapsed && (
        <div className="proposal-phase-panel-body">
          <p className="proposal-phase-hint">
            Client signs offered phases only; future phases commit later via change order. Assign
            structures with the phase selector on each card.
          </p>

          <div className="proposal-phase-list">
            {ordered.map((phase, idx) => {
              const phaseNodeSets = getPhaseNodeSets(proposalLike, phase.id);
              const amount = computePhaseCommitAmount(proposalLike, phase.id);
              const committed = phase.status === 'committed';
              return (
                <div key={phase.id} className={`proposal-phase-row status-${phase.status}`}>
                  <div className="proposal-phase-row-main">
                    <input
                      className="proposal-phase-name-input"
                      value={phase.name}
                      disabled={committed}
                      onChange={e => onRenamePhase(phase.id, e.target.value)}
                      aria-label={`Phase ${idx + 1} name`}
                    />
                    <span className={`proposal-phase-status-badge status-${phase.status}`}>
                      {getPhaseStatusLabel(phase.status)}
                    </span>
                  </div>
                  <div className="proposal-phase-row-meta">
                    <span className="proposal-phase-structures">
                      {phaseNodeSets.length === 0
                        ? 'No structures'
                        : phaseNodeSets.map(ns => ns.name).join(', ')}
                    </span>
                    <span className="proposal-phase-structure-count">
                      {phaseNodeSets.length === 0
                        ? 'No structures'
                        : `${phaseNodeSets.length} structure${phaseNodeSets.length === 1 ? '' : 's'}`}
                    </span>
                    <span className="proposal-phase-subtotal" title="Phase billable subtotal (structure pricing × margin)">
                      {fmt(amount)}
                    </span>
                  </div>
                  <div className="proposal-phase-row-actions">
                    {!committed && (
                      <select
                        className="proposal-phase-status-select"
                        value={phase.status}
                        onChange={e => onSetPhaseStatus(phase.id, e.target.value as ProposalPhaseStatus)}
                        title="Offered phases are signable by the client; future phases show as estimates"
                      >
                        <option value="offered">Offered</option>
                        <option value="future">Future</option>
                      </select>
                    )}
                    <button
                      type="button"
                      className="proposal-phase-icon-btn"
                      disabled={committed || idx === 0}
                      onClick={() => onMovePhase(phase.id, -1)}
                      title="Move phase up"
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      type="button"
                      className="proposal-phase-icon-btn"
                      disabled={committed || idx === ordered.length - 1}
                      onClick={() => onMovePhase(phase.id, 1)}
                      title="Move phase down"
                    >
                      <ArrowDown size={13} />
                    </button>
                    <button
                      type="button"
                      className="proposal-phase-icon-btn danger"
                      disabled={committed || ordered.length <= 1}
                      onClick={() => onDeletePhase(phase.id)}
                      title={
                        committed
                          ? 'Committed phases cannot be deleted'
                          : 'Delete phase (its structures move to the first phase)'
                      }
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="proposal-phase-footer">
            <button type="button" className="proposal-phase-add-btn" onClick={onAddPhase}>
              <Plus size={14} /> Add phase
            </button>
            {proposalApproved && (
              <span className="proposal-phase-locked-note">
                Signed proposal — committed phases are locked; later phases are offered from Client
                Portal Management.
              </span>
            )}
          </div>

          {problems.length > 0 && (
            <ul className="proposal-phase-problems">
              {problems.map(p => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ProposalPhasePanel;
